import { Card, CardActionArea, ListItem, Stack, Typography } from '@mui/material';
import { EmailOutlined } from '@mui/icons-material';
import { FgaUserState, type FgaUser } from '@/generated/model';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';

import { useCurrentUser } from '../../hooks/useCurrentUser';
import { getUserDisplayName } from '../../utils/userNameUtils';
import { userAccessRoutes } from '../../utils/userAccessRoutes';
import { RoleChip } from '../role-chip/RoleChip';
import { UserStatusIcon } from '../user-status-icon/UserStatusIcon';
import { deactivatedUserSx } from './UserListItem.styles';

export function UserListItem({ user }: { user: FgaUser }) {
  const userCtx = useCurrentUser();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isCurrentUser = userCtx?.user.id === user.id;
  const isUserDeactivated = user.state === FgaUserState.DEACTIVATED;
  const displayName = getUserDisplayName(user, i18n.resolvedLanguage ?? i18n.language);

  function handleClick() {
    navigate(userAccessRoutes.userDetails(user.id));
  }

  const userContentSx = isUserDeactivated ? deactivatedUserSx : {};

  return (
    <ListItem component="div" role="listitem" disablePadding sx={{ py: 0.5 }}>
      <Card
        sx={(theme) => ({
          width: '100%',
          border: 2,
          borderColor: isCurrentUser ? theme.palette.info.light : 'divider',
        })}
      >
        <CardActionArea
          onClick={handleClick}
          data-qa-element-id={`user_access_control_user_${user.id}`}
          sx={{
            display: 'flex',
            alignItems: 'start',
            justifyContent: 'space-between',
            p: 1,
          }}
        >
          <Stack gap={0.5} sx={userContentSx}>
            <Stack direction="row" gap={1} alignItems="center">
              <Typography variant="subtitle1">{displayName}</Typography>
              {isCurrentUser && (
                <Typography variant="stateText" color="info.light">
                  {t('userAccessControl.currentProfile')}
                </Typography>
              )}
            </Stack>
            <Typography variant="body2">{user.role.roleObjectName}</Typography>
            {user.role.role && <RoleChip role={user.role.role} isUserDeactivated={isUserDeactivated} />}
            <Stack direction="row" alignItems="center" gap={1}>
              <EmailOutlined fontSize="small" />
              <Typography variant="body2">{user.name}</Typography>
            </Stack>
          </Stack>
          <UserStatusIcon state={user.state} />
        </CardActionArea>
      </Card>
    </ListItem>
  );
}
